import {
  useQuery,
  useMutation,
  useQueryClient,
  useQueries,
  type QueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
  fetchShiftsCalendar,
  fetchShiftById,
  createShift,
  updateShift,
  deleteShift,
  publishShift,
  respondToShift,
  openShiftForClaim,
  closeShiftForClaim,
} from "../api/shiftApi";
import type {
  CreateShiftRequest,
  UpdateShiftRequest,
  ShiftCalendarParams,
} from "./types";

export const shiftKeys = {
  all: ["shifts"] as const,
  calendar: (params: ShiftCalendarParams) =>
    [...shiftKeys.all, "calendar", params] as const,
  detail: (id: number) => [...shiftKeys.all, "detail", id] as const,
};

const SHIFT_CALENDAR_STALE_MS = 15 * 1000;

/**
 * Every mutation below changes what a guard is scheduled for, so every one
 * needs to refresh both the shift-shaped queries (calendar, list, detail) and
 * the Guards panel's weekly-hours query (`["overtime-weekly-hours", ...]`,
 * `entities/overtime-watch`) — a separate key namespace that a plain
 * `invalidateQueries({ queryKey: shiftKeys.all })` never touches. Routing
 * every mutation's onSuccess through this one function means a future
 * mutation can't add a shift-key sweep and forget the hours sweep next to it.
 *
 * `excludeShiftDetailId`, when given, is delete's own carefully-reasoned
 * exception: skip `shiftKeys.detail(id)` so a shift the caller just deleted
 * is never refetched. That refetch is a guaranteed 404 — with this query's
 * default retry: 1, two of them — surfacing as two spurious "not found"
 * toasts on top of the mutation's own success toast instead of just that one.
 */
export function invalidateShiftAndHours(
  queryClient: QueryClient,
  options?: { excludeShiftDetailId?: number }
) {
  const excludeId = options?.excludeShiftDetailId;
  queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] === shiftKeys.all[0] &&
      !(excludeId != null && query.queryKey[1] === "detail" && query.queryKey[2] === excludeId),
  });
  queryClient.invalidateQueries({ queryKey: ["overtime-weekly-hours"] });
}

interface ShiftsCalendarOptions {
  enabled?: boolean;
  staleTime?: number;
  refetchInterval?: number | false;
  refetchOnMount?: boolean | "always";
}

/**
 * Acceptance counts move from outside this app: a guard accepts or self-claims on
 * their phone and nothing here invalidates. With a two-minute stale window and the
 * app-wide refetchOnWindowFocus off, tabbing back from the phone to the browser was
 * a no-op and the calendar kept showing the pre-acceptance count until a hard reload.
 * Focus is the moment a scheduler looks at coverage, so it is the moment to re-read.
 */
export const useShiftsCalendar = (
  params: ShiftCalendarParams,
  options: ShiftsCalendarOptions = {},
) => {
  return useQuery({
    queryKey: shiftKeys.calendar(params),
    queryFn: () => fetchShiftsCalendar(params),
    enabled: options.enabled,
    staleTime: options.staleTime ?? SHIFT_CALENDAR_STALE_MS,
    refetchOnWindowFocus: true,
    refetchInterval: options.refetchInterval,
    refetchOnMount: options.refetchOnMount,
  });
};

export const useShiftDetail = (id: number) => {
  return useQuery({
    queryKey: shiftKeys.detail(id),
    queryFn: () => fetchShiftById(id),
    enabled: !!id,
  });
};

export const useCreateShift = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateShiftRequest) => createShift(data),
    onSuccess: () => {
      invalidateShiftAndHours(queryClient);
      toast.success("Shift created");
    },
  });
};

export const useUpdateShift = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateShiftRequest }) =>
      updateShift(id, data),
    onSuccess: () => {
      invalidateShiftAndHours(queryClient);
      toast.success("Shift updated");
    },
  });
};

export const useDeleteShift = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      deleteEntireSeries,
    }: {
      id: number;
      deleteEntireSeries?: boolean;
    }) => deleteShift(id, deleteEntireSeries),
    onSuccess: (_data, { id }) => {
      // See invalidateShiftAndHours' own doc for why shiftKeys.detail(id) is
      // deliberately excluded here: every caller reachable from a delete (the
      // shift's own page, an edit modal still mounted behind the confirm
      // dialog, a dashboard row) may still have that exact query active, and
      // refetching a shift that no longer exists is a guaranteed 404.
      invalidateShiftAndHours(queryClient, { excludeShiftDetailId: id });
      toast.success("Shift deleted");
    },
  });
};

export const usePublishShift = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      publishEntireSeries,
    }: {
      id: number;
      publishEntireSeries?: boolean;
    }) => publishShift(id, publishEntireSeries),
    onSuccess: () => {
      invalidateShiftAndHours(queryClient);
      toast.success("Shift published");
    },
  });
};

export const useRespondToShift = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      response,
    }: {
      id: number;
      response: "accept" | "reject";
    }) => respondToShift(id, response),
    onSuccess: () => {
      invalidateShiftAndHours(queryClient);
      toast.success("Response submitted");
    },
  });
};

export const useOpenShiftForClaim = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => openShiftForClaim(id),
    onSuccess: () => {
      invalidateShiftAndHours(queryClient);
      toast.success("Shift published for self-claim");
    },
  });
};

export const useCloseShiftForClaim = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => closeShiftForClaim(id),
    onSuccess: () => {
      invalidateShiftAndHours(queryClient);
      toast.success("Self-claim closed");
    },
  });
};

export const useSiteShiftsCoverage = (
  siteIds: number[],
  fromDate: string,
  toDate: string,
) => {
  const results = useQueries({
    queries: siteIds.map((siteId) => ({
      queryKey: shiftKeys.calendar({ fromDate, toDate, siteId }),
      queryFn: () => fetchShiftsCalendar({ fromDate, toDate, siteId }),
      // Same freshness as the calendar, but no focus refetch: this fans out one
      // request per site, so a focus burst here costs a multiple of the calendar's.
      staleTime: SHIFT_CALENDAR_STALE_MS,
      enabled: !!fromDate && !!toDate,
    })),
  });

  const shifts = results.flatMap((r) => r.data ?? []);
  const anyError = results.some((r) => r.isError);
  return {
    shifts,
    isLoading: results.some((r) => r.isLoading) && shifts.length === 0,
    isError: anyError && shifts.length === 0,
    isPartialError: anyError && shifts.length > 0,
  };
};
